import React, { ComponentClass, FC } from "react";
import { useBlocker } from "react-router-dom";
import { ChangesTrackingService } from "./ChangesTrackingService";

export const ChangesTrackerContext = React.createContext(
  new ChangesTrackingService()
);

export default function withChangesTracker<P extends object>(
  WrappedComponent: ComponentClass<P> | FC<React.PropsWithChildren<P>>
): FC<React.PropsWithChildren<P>> {
  return function ChangesTracker(props: Readonly<P>) {
    const changesTrackingService = React.useMemo(
      () => new ChangesTrackingService(),
      []
    );
    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
      const pathChanged = currentLocation.pathname !== nextLocation.pathname;

      return (
        pathChanged && Boolean(changesTrackingService.getValidationMessage())
      );
    });

    React.useEffect(() => {
      const preventClosing = (event: BeforeUnloadEvent) => {
        const result = changesTrackingService.getValidationMessage();

        if (result) {
          event.preventDefault();
          event.returnValue = result;
        }
      };

      window.addEventListener("beforeunload", preventClosing);

      return () => {
        window.removeEventListener("beforeunload", preventClosing);
      };
    }, [changesTrackingService]);

    React.useEffect(() => {
      if (blocker.state !== "blocked") {
        return;
      }

      const message = changesTrackingService.getValidationMessage();

      if (message && window.confirm(message.toString())) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }, [blocker, changesTrackingService]);

    return (
      <ChangesTrackerContext.Provider value={changesTrackingService}>
        <WrappedComponent {...props} />
      </ChangesTrackerContext.Provider>
    );
  };
}
